import React from 'react';
import { Link } from 'react-router-dom';

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="container py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="font-heading text-4xl font-bold text-primary-700 mb-4">
              Cookie Policy
            </h1>
            <p className="text-lg text-text-muted">
              Last updated: {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>

          {/* Content */}
          <div className="card p-8 space-y-8">
            <section>
              <h2 className="font-heading text-2xl font-bold text-primary-700 mb-4">
                1. What Are Cookies?
              </h2>
              <p className="text-text-muted leading-relaxed mb-4">
                Cookies are small text files that are placed on your device (computer, tablet, or mobile) 
                when you visit our website. They help us provide you with a better experience by:
              </p>
              <ul className="list-disc list-inside text-text-muted space-y-2">
                <li>Remembering your preferences and settings</li>
                <li>Analyzing how our website is used</li>
                <li>Improving website functionality and performance</li>
                <li>Providing personalized content and services</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold text-primary-700 mb-4">
                2. Types of Cookies We Use
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="font-heading text-xl font-semibold text-primary-600 mb-2">
                    Essential Cookies
                  </h3>
                  <p className="text-text-muted leading-relaxed mb-2">
                    These cookies are necessary for the website to function properly. They enable basic 
                    functions like page navigation, form submissions, and security features.
                  </p>
                  <ul className="list-disc list-inside text-text-muted space-y-1">
                    <li>Session management and user authentication</li>
                    <li>Form submission and validation</li>
                    <li>Security and fraud prevention</li>
                    <li>Website accessibility features</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-primary-600 mb-2">
                    Performance Cookies
                  </h3>
                  <p className="text-text-muted leading-relaxed mb-2">
                    These cookies help us understand how visitors interact with our website by collecting 
                    and reporting information anonymously.
                  </p>
                  <ul className="list-disc list-inside text-text-muted space-y-1">
                    <li>Page load times and performance metrics</li>
                    <li>User navigation patterns and behavior</li>
                    <li>Error tracking and debugging information</li>
                    <li>Website usage statistics</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-primary-600 mb-2">
                    Functionality Cookies
                  </h3>
                  <p className="text-text-muted leading-relaxed mb-2">
                    These cookies allow the website to remember choices you make and provide enhanced, 
                    more personal features.
                  </p>
                  <ul className="list-disc list-inside text-text-muted space-y-1">
                    <li>Language and regional preferences</li>
                    <li>Font size and accessibility settings</li>
                    <li>Form data retention (with your consent)</li>
                    <li>User interface customization</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold text-primary-700 mb-4">
                3. Third-Party Cookies
              </h2>
              <p className="text-text-muted leading-relaxed mb-4">
                Our website may use services from third-party providers that may set their own cookies. 
                These include:
              </p>
              <div className="space-y-4">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-primary-600 mb-2">
                    Formspree (Contact Forms)
                  </h3>
                  <p className="text-text-muted leading-relaxed">
                    We use Formspree to handle contact form submissions. Formspree may use cookies 
                    to ensure proper form functionality and spam protection.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-heading text-lg font-semibold text-primary-600 mb-2">
                    Google Fonts
                  </h3>
                  <p className="text-text-muted leading-relaxed">
                    We use Google Fonts to display custom typography. Google may use cookies 
                    to optimize font delivery and performance.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-heading text-lg font-semibold text-primary-600 mb-2">
                    Font Awesome
                  </h3>
                  <p className="text-text-muted leading-relaxed">
                    We use Font Awesome for icons. Their CDN may use cookies for performance 
                    optimization and analytics.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold text-primary-700 mb-4">
                4. Cookie Duration
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-primary-600 mb-2">
                    Session Cookies
                  </h3>
                  <p className="text-text-muted leading-relaxed">
                    These cookies are temporary and are deleted when you close your browser. 
                    They are used for session management and security.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-heading text-lg font-semibold text-primary-600 mb-2">
                    Persistent Cookies
                  </h3>
                  <p className="text-text-muted leading-relaxed">
                    These cookies remain on your device for a set period or until you delete them. 
                    They remember your preferences and settings for future visits.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold text-primary-700 mb-4">
                5. Managing Your Cookie Preferences
              </h2>
              <p className="text-text-muted leading-relaxed mb-4">
                You have several options for managing cookies:
              </p>
              <div className="space-y-4">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-primary-600 mb-2">
                    Browser Settings
                  </h3>
                  <p className="text-text-muted leading-relaxed">
                    Most browsers allow you to control cookies through their settings. You can:
                  </p>
                  <ul className="list-disc list-inside text-text-muted mt-2 space-y-1">
                    <li>Block all cookies</li>
                    <li>Allow cookies only from trusted sites</li>
                    <li>Delete existing cookies</li>
                    <li>Set up notifications when cookies are set</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-heading text-lg font-semibold text-primary-600 mb-2">
                    Essential Cookies Notice
                  </h3>
                  <p className="text-text-muted leading-relaxed">
                    Please note that disabling essential cookies may affect the functionality of our website. 
                    Some features may not work properly if cookies are disabled.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold text-primary-700 mb-4">
                6. Medical Website Considerations
              </h2>
              <p className="text-text-muted leading-relaxed mb-4">
                As a medical website, we take extra precautions to protect your privacy:
              </p>
              <ul className="list-disc list-inside text-text-muted space-y-2">
                <li>We do not use tracking cookies for advertising purposes</li>
                <li>No personal health information is stored in cookies</li>
                <li>All cookie data is encrypted and securely transmitted</li>
                <li>We comply with healthcare privacy regulations</li>
                <li>Cookie usage is minimal and focused on essential functionality</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold text-primary-700 mb-4">
                7. Updates to This Policy
              </h2>
              <p className="text-text-muted leading-relaxed mb-4">
                We may update this Cookie Policy from time to time to reflect changes in our practices 
                or for other operational, legal, or regulatory reasons. We will notify you of any 
                material changes by posting the updated policy on our website.
              </p>
              <p className="text-text-muted leading-relaxed">
                The "Last updated" date at the top of this policy indicates when it was last revised.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold text-primary-700 mb-4">
                8. Contact Information
              </h2>
              <p className="text-text-muted leading-relaxed mb-4">
                If you have any questions about our use of cookies or this Cookie Policy, please contact us:
              </p>
              <div className="bg-primary-50 rounded-lg p-6">
                <div className="space-y-2">
                  <p className="text-text-muted">
                    <strong>Email:</strong> contact@adeshpalyekar.com
                  </p>
                  <p className="text-text-muted">
                    <strong>Phone:</strong> +91 8080485895
                  </p>
                  <p className="text-text-muted">
                    <strong>Address:</strong> Konkan Cancer and Multispeciality Hospital, Kudal, Maharashtra
                  </p>
                </div>
              </div>
            </section>

            {/* Back to Home */}
            <div className="text-center pt-8">
              <Link 
                to="/" 
                className="btn-primary inline-flex items-center gap-2"
              >
                <i className="fas fa-arrow-left"></i>
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy; 